/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { createI18n } from 'vue-i18n'

// Import locale messages
import en from '@/locales/en.json'
import fr from '@/locales/fr.json'
import es from '@/locales/es.json'

export type MessageLanguages = keyof typeof messages
export type MessageSchema = typeof en

const messages = {
  en,
  fr,
  es,
}

// Get the user's preferred language from localStorage or browser
function getDefaultLocale(): string {
  const stored = localStorage.getItem('noorwriter-locale')
  if (stored && Object.keys(messages).includes(stored)) {
    return stored
  }
  
  const browserLang = navigator.language.split('-')[0]
  if (Object.keys(messages).includes(browserLang)) {
    return browserLang
  }
  
  return 'en' // fallback
}

const i18n = createI18n<MessageSchema, MessageLanguages, false>({
  legacy: false,
  locale: getDefaultLocale(),
  fallbackLocale: 'en',
  messages,
  globalInjection: true,
})

// Function to change locale and persist it
export function setLocale(locale: MessageLanguages) {
  if (Object.keys(messages).includes(locale)) {
    i18n.global.locale.value = locale
    localStorage.setItem('noorwriter-locale', locale)

    // Update document language attribute
    document.documentElement.lang = locale
  }
}

// Initialize document language on load
document.documentElement.lang = i18n.global.locale.value

export default i18n
